/**
 * Meta App Review checklist — admin-only.
 *
 * Surfaces, per Meta connection, which gated scopes are granted vs missing
 * vs skipped (resource not present) vs error. The probes are run live by
 * GET /api/integrations/meta/scope-audit and the page just renders the
 * response. Each missing scope shows the precise Meta error code plus a
 * ready-to-paste use-case description for the Meta App Review request.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ShieldCheck, CheckCircle2, XCircle, AlertTriangle, Minus, RefreshCw,
  Loader2, ExternalLink, ChevronDown, ChevronRight, Copy, FileCheck,
  Database, User,
} from "lucide-react";

// Static copy templates for each scope — pasted into Meta App Review forms.
// Phrased to match Meta's "specific, user-facing use case" guidance.
const USE_CASES = {
  pages_show_list: "Used to discover which Facebook Pages each agency-partner client has granted us access to, so our admins can assign each Page to the matching client record in our agency dashboard.",
  business_management: "As a marketing agency we are added as a Partner to each client's Business Manager. We use business_management to walk /me/businesses once on master OAuth and enumerate the Ad Accounts, Pages, IG accounts, and WhatsApp assets each Partner client has shared with us, so our admins can map each asset to the correct client in our dashboard.",
  pages_read_engagement: "Iceberg Digital is a marketing agency that produces monthly performance reports for our clients. We read each post's reactions, comments and shares totals so the report can show how each piece of organic Facebook content performed alongside its caption + image.",
  pages_read_user_content: "Our monthly client report embeds — with image, full caption text, and engagement counts — every organic post published on the client's Facebook Page during the report month. pages_read_user_content gives us read access to the post message and full_picture so the report shows the actual content the client published.",
  read_insights: "Our agency platform pulls organic per-post insights (impressions, reach, engaged_users, post_clicks) and page-level insights (page_impressions, page_post_engagements) for each Page the client has granted us access to. These metrics appear on each post card in the monthly client report and are used to inform the next month's content strategy recommendations our team writes.",
  instagram_basic: "When a client's Facebook Page has a linked Instagram Business account, our agency dashboard fetches the IG profile (username, followers, follows, media count) and recent media to include alongside Facebook in the client's monthly report.",
  instagram_manage_insights: "We pull per-IG-media insights (reach, impressions, engagement, saves, plays, total_interactions) for each Instagram Business post published during the report month, so each post card in the client's monthly PDF report shows how that post performed.",
  ads_read: "We read each client's Ad Account insights (campaigns, ads, spend, impressions, clicks, conversions) for the report month to populate the Meta Ads performance section of the client's monthly PDF report and inform our team's optimisation recommendations.",
};

const STATUS_STYLES = {
  ok: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", icon: CheckCircle2, iconClass: "text-emerald-600", label: "Granted" },
  missing: { bg: "bg-red-50", border: "border-red-200", text: "text-red-900", icon: XCircle, iconClass: "text-red-600", label: "Missing" },
  skipped: { bg: "bg-zinc-50", border: "border-zinc-200", text: "text-zinc-700", icon: Minus, iconClass: "text-zinc-400", label: "Skipped" },
  error: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", icon: AlertTriangle, iconClass: "text-amber-600", label: "Error" },
};

export default function MetaAppReviewPage() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);

  const runAudit = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/meta/scope-audit",
        { timeout: 90_000 });
      setAudit(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed — see console");
    } finally { setLoading(false); }
  };

  useEffect(() => { runAudit(); }, []);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="meta-app-review-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-zinc-500 text-[11px] uppercase tracking-[0.18em]">
            <Link to="/integrations/meta" className="hover:text-[#1877F2]">Meta</Link>
            <ChevronRight className="h-3 w-3" />
            <span>App Review</span>
          </div>
          <h1 className="text-3xl font-bold flex items-center gap-2 mt-1">
            <ShieldCheck className="h-7 w-7 text-[#1877F2]" />
            Meta App Review checklist
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-3xl">
            Live probes hit each gated Meta scope and surface exactly which
            permissions are granted vs missing for every Meta connection in
            this workspace. Use this page to assemble your App Review request.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {audit?.generated_at && (
            <span className="text-[11px] font-mono text-zinc-500">
              {new Date(audit.generated_at).toLocaleString()}
            </span>
          )}
          <Button size="sm" onClick={runAudit} disabled={loading}
            className="rounded-sm bg-[#1877F2] hover:bg-[#0c5dc7] text-white"
            data-testid="audit-refresh">
            {loading
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Re-run audit
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {audit && (audit.connections || []).length === 0 && (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-8 text-center">
            <Database className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-1">No Meta connections yet</h3>
            <p className="text-sm text-zinc-500 mb-4">
              Connect Meta on the master integrations page (or any client&apos;s Meta
              Ads page) to start auditing scopes.
            </p>
            <Link to="/integrations/meta">
              <Button className="rounded-sm bg-[#1877F2] hover:bg-[#0c5dc7] text-white"
                data-testid="audit-go-to-meta">
                Go to Meta master integration
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Rollup — scope-by-scope across all connections */}
      {audit && (audit.connections || []).length > 0 && (
        <RollupCard rollup={audit.rollup || []} />
      )}

      {/* Per-connection probe results */}
      {audit && (audit.connections || []).map((conn) => (
        <ConnectionCard key={conn.connection_id} conn={conn} />
      ))}

      {/* Pre-submission checklist */}
      {audit && (audit.connections || []).length > 0 && (
        <PreSubmissionChecklist />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.error;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider ${s.bg} ${s.border} ${s.text}`}>
      <Icon className={`h-3 w-3 ${s.iconClass}`} /> {s.label}
    </span>
  );
}

function RollupCard({ rollup }) {
  const missingScopes = rollup.filter((r) => r.missing_count > 0);
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="audit-rollup">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="label-overline">Scope rollup · across all connections</div>
            <h3 className="text-lg font-bold">
              {missingScopes.length === 0
                ? "All scopes granted on every connection 🎉"
                : `${missingScopes.length} scope(s) need App Review`}
            </h3>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {rollup.map((r) => {
            const allOk = r.granted_count === r.total && r.total > 0;
            const noneOk = r.granted_count === 0 && r.missing_count > 0;
            const tint = allOk ? "border-emerald-200 bg-emerald-50/40"
                              : noneOk ? "border-red-200 bg-red-50/40"
                                       : "border-amber-200 bg-amber-50/40";
            return (
              <div key={r.scope}
                className={`rounded-sm border p-3 ${tint}`}
                data-testid={`rollup-${r.scope}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px] text-zinc-700">{r.scope}</span>
                  <span className="font-mono text-[10px] text-zinc-500">
                    {r.granted_count} / {r.total} OK
                  </span>
                </div>
                <div className="text-[11px] text-zinc-700 mt-0.5">{r.label}</div>
                <div className="flex gap-2 mt-1.5 text-[10px] font-mono">
                  {r.granted_count > 0 && (
                    <span className="text-emerald-700">✓{r.granted_count}</span>
                  )}
                  {r.missing_count > 0 && (
                    <span className="text-red-700">✗{r.missing_count}</span>
                  )}
                  {r.skipped_count > 0 && (
                    <span className="text-zinc-500">−{r.skipped_count}</span>
                  )}
                  {r.error_count > 0 && (
                    <span className="text-amber-700">!{r.error_count}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function ConnectionCard({ conn }) {
  const [expanded, setExpanded] = useState(true);
  const missingCount = (conn.probes || []).filter((p) => p.status === "missing").length;
  const grantedCount = (conn.probes || []).filter((p) => p.status === "ok").length;
  return (
    <Card className="rounded-sm border-zinc-200"
      data-testid={`audit-conn-${conn.connection_id}`}>
      <CardContent className="p-5 space-y-3">
        <button type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between gap-3 flex-wrap text-left">
          <div className="flex items-center gap-2">
            {conn.kind === "master"
              ? <Database className="h-5 w-5 text-zinc-700" />
              : <User className="h-5 w-5 text-[#1877F2]" />}
            <div>
              <div className="font-bold text-base">{conn.label}</div>
              <div className="text-[11px] font-mono text-zinc-500">
                {conn.meta_user_name} {conn.meta_user_email ? `· ${conn.meta_user_email}` : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 font-mono text-[10px]">
              ✓ {grantedCount} granted
            </Badge>
            {missingCount > 0 && (
              <Badge className="rounded-sm bg-red-100 text-red-800 border-red-200 font-mono text-[10px]">
                ✗ {missingCount} missing
              </Badge>
            )}
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
        </button>

        {conn.error && (
          <div className="rounded-sm border border-red-200 bg-red-50 p-2 text-[11px] text-red-900">
            {conn.error}
          </div>
        )}

        {expanded && (
          <>
            <div className="flex gap-3 text-[10px] font-mono text-zinc-500 flex-wrap">
              <span>scopes declared: {conn.scopes_declared?.join(", ") || "—"}</span>
              {conn.sample_resources?.page_id && (
                <span>· probed page: <code>{conn.sample_resources.page_id}</code></span>
              )}
              {conn.sample_resources?.ig_user_id && (
                <span>· probed IG: <code>{conn.sample_resources.ig_user_id}</code></span>
              )}
              {conn.sample_resources?.ad_account_id && (
                <span>· probed ad: <code>{conn.sample_resources.ad_account_id}</code></span>
              )}
            </div>

            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid={`audit-conn-table-${conn.connection_id}`}>
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-2">Scope</th>
                    <th className="text-left px-3 py-2">Capability</th>
                    <th className="text-left px-3 py-2 w-[110px]">Status</th>
                    <th className="text-left px-3 py-2">Meta response</th>
                  </tr>
                </thead>
                <tbody>
                  {(conn.probes || []).map((p) => (
                    <ProbeRow key={p.scope} probe={p} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ProbeRow({ probe }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t border-zinc-100 hover:bg-zinc-50/50 cursor-pointer"
        onClick={() => setOpen((v) => !v)}>
        <td className="px-3 py-2 align-top">
          <div className="font-mono text-[11px] text-zinc-700">{probe.scope}</div>
        </td>
        <td className="px-3 py-2 align-top">
          <div className="text-[12px]">{probe.label}</div>
          <div className="text-[11px] text-zinc-500">{probe.description}</div>
        </td>
        <td className="px-3 py-2 align-top">
          <StatusPill status={probe.status} />
        </td>
        <td className="px-3 py-2 align-top">
          {probe.error_message ? (
            <div className="font-mono text-[10px] text-zinc-700 break-words">
              {probe.error_code && <span className="text-zinc-400">(#{probe.error_code}) </span>}
              {probe.error_message.slice(0, 200)}
            </div>
          ) : (
            <span className="text-[11px] text-emerald-700">OK · 200</span>
          )}
          {probe.status === "missing" && (
            <div className="mt-1">
              <span className="text-[10px] text-[#1877F2] underline">
                {open ? "Hide use-case template" : "Show use-case template for App Review →"}
              </span>
            </div>
          )}
        </td>
      </tr>
      {open && probe.status === "missing" && (
        <tr className="bg-blue-50/40">
          <td colSpan={4} className="px-3 py-3">
            <UseCaseTemplate scope={probe.scope} />
          </td>
        </tr>
      )}
    </>
  );
}

function UseCaseTemplate({ scope }) {
  const text = USE_CASES[scope] || "(no template — describe how your app uses this scope)";
  const copy = () => {
    navigator.clipboard.writeText(text);
    toast.success("Copied — paste into Meta App Review form");
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-zinc-600">
          Suggested use-case description for App Review
        </div>
        <Button size="sm" variant="outline" onClick={copy}
          className="rounded-sm h-7 text-[11px]">
          <Copy className="h-3 w-3 mr-1" /> Copy
        </Button>
      </div>
      <p className="text-[12px] text-zinc-800 bg-white border border-zinc-200 rounded-sm p-3 leading-relaxed">
        {text}
      </p>
      <div className="text-[11px] text-zinc-500">
        Submit this in Meta App Dashboard → <span className="font-semibold">App Review → Requests</span> →
        <span className="font-mono"> {scope}</span> → {`"How will your app use ${scope}?"`}
      </div>
    </div>
  );
}

function PreSubmissionChecklist() {
  const items = [
    {
      label: "Privacy Policy URL publicly reachable",
      detail: "Meta refuses any review request without a working Privacy Policy URL. Document how your app uses Meta data.",
    },
    {
      label: "Terms of Service URL publicly reachable",
      detail: "Required alongside the privacy policy in App Dashboard → Settings → Basic.",
    },
    {
      label: "App Icon uploaded (1024 × 1024 PNG)",
      detail: "Settings → Basic. Reviewers reject submissions without one.",
    },
    {
      label: "App set to Live (not Development mode)",
      detail: "Toggle in the top bar of App Dashboard. Without this, advanced scopes can't be granted to non-Developer users.",
    },
    {
      label: "Data Deletion Callback URL or instructions URL set",
      detail: "Required for user data scopes — point at /legal/meta-data-deletion or similar.",
    },
    {
      label: "Business Verification completed in Business Manager",
      detail: "Required for business_management + ads_read + insights scopes. You already have a verified BM (Conex Imagetraders).",
    },
    {
      label: "Test admin login provided in App Review notes",
      detail: "Reviewers need to actually log into your app. Provide the read-only reviewer account credentials you set up in Admin → Access — do NOT re-use your master admin login.",
    },
    {
      label: "Screencast (1-2 minutes) attached to each request",
      detail: "Show the OAuth flow + the specific feature that uses the requested scope (e.g. the Social Posts section in a monthly report for pages_read_user_content).",
    },
  ];
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="audit-pre-submission">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <FileCheck className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-bold">Pre-submission checklist</h3>
        </div>
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.label} className="flex gap-3 items-start p-2 rounded-sm hover:bg-zinc-50">
              <div className="mt-0.5 w-4 h-4 rounded-sm border border-zinc-300 shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold">{it.label}</div>
                <div className="text-[11px] text-zinc-600 mt-0.5">{it.detail}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-zinc-100 text-[12px] text-zinc-600">
          Once everything above is ticked, head to{" "}
          <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer"
             className="text-[#1877F2] underline inline-flex items-center gap-1">
            Meta App Dashboard <ExternalLink className="h-3 w-3" />
          </a>{" "}
          → App Review → Requests, and submit each missing scope with the copied
          use-case text. Typical review time: 3-10 business days.
        </div>
      </CardContent>
    </Card>
  );
}
